/**
 * "Quem faz?" (owner, 10/10/2026; docs/refactor/28): Analisar, O que falta and the audits go to whoever the person
 * chooses — one of their agents on this computer (Claude, Codex…), another of their computers, a Claude Code session
 * that is listening, or a teammate who accepts work from the team. Alone with one agent, it runs straight away; with
 * more than one choice, a short dialog asks who, with the person's own agent first and chosen.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Bot, Monitor, RadioTower, User } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { isDesktop } from '../lib/desktop'
import { detectAgents } from '../lib/localAgent'
import { thisMachineId } from '../lib/runnerHost'
import type { WorkTarget } from '../lib/prompts'
import type { RemoteAgent } from '../lib/types'
import { Modal } from './Modal'

export interface WhoOption {
  key: string
  label: string
  hint?: string
  icon: 'agent' | 'machine' | 'session' | 'person'
  target: WorkTarget
}

/** The agents installed on this computer: slow to find (it asks each one), so found ahead and kept a while. */
const LOCAL_AGENTS = ['who-local-agents']
const LOCAL_AGENTS_FRESH = 5 * 60_000

/** A Claude Code session counts as listening when it asked for work in the last few minutes. */
const LISTENING_MS = 5 * 60_000

/** Who can do a piece of work in this repository, the person's own agent first. */
export async function whoCanDoIt(
  fetch: <T>(key: unknown[], fn: () => Promise<T>) => Promise<T>,
  repository: { id: string; name: string } | null,
  t: (key: string, vars?: Record<string, string | number>) => string,
  now = Date.now(),
): Promise<WhoOption[]> {
  const settle = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback)
  const [agents, machines, remote, mates, plan] = await Promise.all([
    settle(fetch(LOCAL_AGENTS, detectAgents), []),
    settle(fetch(['machines'], api.promptQueue.machines), []),
    settle(fetch(['remote'], api.remote.overview), null),
    settle(fetch(['teammates', repository?.id ?? null], () => api.workOrders.teammates(repository?.id)), []),
    settle(fetch(['billing'], api.billing.state), null),
  ])
  const options: WhoOption[] = []
  const here = isDesktop() ? thisMachineId() : null

  for (const agent of agents.filter((a) => a.runnable)) {
    options.push({ key: `agent:${agent.id}`, label: t('who.thisComputer', { agent: agent.name }), icon: 'agent', target: { machine: here, agent: agent.id } })
  }
  for (const machine of machines.filter((m) => m.online && m.id !== here)) {
    const name = machine.name ?? t('who.otherComputer')
    const there = machine.agents ?? []
    // More than one agent there: each one, by name ("Codex CLI · CASA-PC"); otherwise the computer, with its own.
    if (there.length > 1) {
      for (const agent of there) {
        options.push({ key: `machine:${machine.id}:${agent}`, label: `${AGENT_NAMES[agent] ?? agent} · ${name}`, hint: t('who.machineAgentHint'), icon: 'machine', target: { machine: machine.id, agent } })
      }
    } else {
      options.push({ key: `machine:${machine.id}`, label: name, hint: t('who.machineHint'), icon: 'machine', target: { machine: machine.id } })
    }
  }
  // A desktop app's own line ("desktop-…") is its computer, offered above.
  const listening = (a: RemoteAgent) =>
    !a.clientSessionId.startsWith('desktop-') && (a.waiting || a.working) && now - new Date(a.lastPolledAt).getTime() < LISTENING_MS && (!a.repository || !repository || a.repository === repository.name)
  // A session hears only while commands from elsewhere are on.
  for (const session of (remote?.enabled ? remote.agents : []).filter(listening)) {
    options.push({
      key: `session:${session.clientSessionId}`,
      label: t('who.session', { id: session.clientSessionId.slice(0, 6) }),
      hint: session.working ? t('who.sessionBusy') : t('who.sessionWaiting'),
      icon: 'session',
      target: { listener: session.clientSessionId },
    })
  }
  // A teammate's agent is the Team plan's (docs/refactor/40): not offered on a plan that would refuse it. When the
  // plan cannot be read, the server still decides.
  if (plan && !plan.entitlements.teamWork) return options
  // Somebody in two of the person's teams comes back twice: offered once.
  const seen = new Set<string>()
  for (const mate of mates.filter((m) => !m.isMe && m.acceptsTeamWork && (m.remoteOn || m.desktopOn))) {
    if (seen.has(mate.developerId)) continue
    seen.add(mate.developerId)
    options.push({ key: `person:${mate.developerId}`, label: mate.name, hint: t('who.personHint'), icon: 'person', target: { builderDeveloperId: mate.developerId } })
  }
  return options
}

const ICONS = { agent: Bot, machine: Monitor, session: RadioTower, person: User }

const AGENT_NAMES: Record<string, string> = { claude: 'Claude Code', 'claude-2': 'Claude Code #2', codex: 'Codex CLI', gemini: 'Gemini CLI', opencode: 'OpenCode' }

/**
 * `ask(run)`: runs `run` with the one who can do it, or asks first when there is a choice.
 * Render `dialog` where the button is.
 */
export function useWhoDoesIt(repository: { id: string; name: string } | null): { ask: (run: (target: WorkTarget, chosen?: WhoOption) => void) => Promise<void>; dialog: ReactNode } {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<{ options: WhoOption[]; run: (target: WorkTarget, chosen?: WhoOption) => void } | null>(null)
  const [chosen, setChosen] = useState('')
  // Read when asked, so `ask` stays the same function while the repository does not change.
  const id = repository?.id ?? null
  const name = repository?.name ?? ''

  useEffect(() => {
    if (isDesktop()) void queryClient.prefetchQuery({ queryKey: LOCAL_AGENTS, queryFn: detectAgents, staleTime: LOCAL_AGENTS_FRESH })
  }, [queryClient])

  const ask = useCallback(
    async (run: (target: WorkTarget, chosen?: WhoOption) => void) => {
      const fetch = <T,>(key: unknown[], fn: () => Promise<T>) => queryClient.fetchQuery({ queryKey: key, queryFn: fn, staleTime: key === LOCAL_AGENTS ? LOCAL_AGENTS_FRESH : 15_000, retry: false })
      const options = await whoCanDoIt(fetch, id ? { id, name } : null, t)
      // One way only: the usual route, as before there was a choice.
      if (options.length <= 1) {
        run({}, options[0])
        return
      }
      setChosen(options[0]!.key)
      setPending({ options, run })
    },
    [queryClient, id, name, t],
  )

  const close = useCallback(() => setPending(null), [])
  const dialog = pending && (
    <Modal title={t('who.title')} onClose={close}>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          const option = pending.options.find((o) => o.key === chosen) ?? pending.options[0]!
          setPending(null)
          pending.run(option.target, option)
        }}
      >
        <p className="small muted" style={{ margin: 0 }}>{t('who.body')}</p>
        <div className="who-list" role="radiogroup" aria-label={t('who.title')}>
          {pending.options.map((option) => {
            const Icon = ICONS[option.icon]
            return (
              <label key={option.key} className={`who-option${chosen === option.key ? ' is-chosen' : ''}`}>
                <input type="radio" name="who" value={option.key} checked={chosen === option.key} onChange={() => setChosen(option.key)} />
                <Icon size={15} aria-hidden />
                <span className="who-option__label">{option.label}</span>
                {option.hint && <span className="tiny muted">{option.hint}</span>}
              </label>
            )
          })}
        </div>
        <div className="row row--end">
          <button type="button" className="btn btn--ghost" onClick={close}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn--primary">{t('who.run')}</button>
        </div>
      </form>
    </Modal>
  )
  return { ask, dialog }
}
